<template>
  <v-container class="ma-sm-auto">
    <v-row class="mb-3" no-gutters v-if="$vuetify.breakpoint.mdAndUp"><h1 class="header font-weight-bold">{{ $t('signup.header') }}</h1></v-row>
    <v-row :class="{'mainBorder': $vuetify.breakpoint.mdAndUp}" no-gutters>
      <v-col class="primary white--text pb-4" cols="4" v-if="$vuetify.breakpoint.mdAndUp">
        <div class="d-flex flex-column align-center justify-center fill-height">
          <h3 class="text-uppercase font-weight-bold mb-1 text-h6">{{ $t('signup.welcome') }}</h3>
          {{ $t('already_have_account') }}
          <v-btn color="white" class="primary--text my-6" :href="loginUrl" data-testid="a_1282dee2-6eee-45be-91e3-1fc237943683">{{ $t('signup.sign_in') }}</v-btn>
        </div>
      </v-col>
      <v-col :cols="$vuetify.breakpoint.mdAndUp ? 8 : 12" class="pt-0 pr-0">
        <r-form-card class="white">
          <div class="text-center">
            <img src="static/images/vevox-logo.svg" class="logo" v-if="$vuetify.breakpoint.smAndDown"/>
          </div>
          <div class="stepperHeader d-flex align-center my-3 my-sm-4">
             <div class="stepNumber rounded-xl secondary white--text font-weight-medium" style="padding-top: 2px">
              1
            </div>
            <span class="secondary--text ml-2 font-weight-bold">{{ $t('signup.step1_header') }}</span>
            <hr class="secondary mx-2 flex-grow-1" />
            <div class="stepNumber rounded-xl grey darken-2 white--text font-weight-medium" style="padding-top: 2px">
              2
            </div>
            <span class="grey--text text--darken-2 ml-2 font-weight-bold">{{ $t('signup.step2_header') }}</span>
          </div>
          <v-form ref="signupform" v-model="valid" lazy-validation >
            <h2 class="mb-3 text-h6">{{ $t(this.createTitleText) }}</h2>
            <div class="d-flex justify-space-between">
              <v-text-field outlined dense v-model="firstName" class="mr-4" :rules="nameRules" ref="firstname" v-bind:label="$t('first_name')"
                data-testid="firstname-input" autocomplete="given-name" counter="40"></v-text-field>
              <v-text-field outlined dense v-model="lastName" :rules="nameRules" ref="lastname" v-bind:label="$t('last_name')"
                data-testid="lastname-input" autocomplete="family-name" counter="40"></v-text-field>
            </div>
            <v-text-field outlined dense v-model="email" :rules="emailRules" ref="email" v-bind:label="$t('email')" v-on:blur="validateEmail()" data-testid="email-input"
              :error="emailError.length > 0" :error-messages="emailError" :hint="$t('signup.email_hint')" autocomplete="email"></v-text-field>
            <v-text-field outlined dense v-model="password" :rules="passwordRules" v-bind:label="$t('password')" data-testid="password-input"
              :type="showPassword ? 'text' : 'password'" :append-icon="showPassword ? 'visibility' : 'visibility_off'"
              @click:append="showPassword = !showPassword" :hint="$t('signup.password_hint')" autocomplete="new-password"></v-text-field>
            <v-alert :value="error.length > 0" dense text type="error" data-testid="v-alert_45d8edf0-4c80-4a6a-99fd-4dfee5165fb9">
              {{error}}
            </v-alert>

            <span class="text-body-1">{{ $t('by_signing_up_you_agree') }} <a target="_blank" href="https://www.vevox.com/terms-conditions" data-testid="a_7e92feff-a462-40bc-a66c-5186ab7cf18b">{{ $t('terms_and_conditions') }}</a></span>

            <div class="buttondiv mt-4 mt-sm-6">
              <!-- ID is used for tracking, do not change! -->
              <r-form-submit-button id="signup_create_account" :disabled="!valid" v-on:click="signup" data-testid="signup-button" class="mx-auto">{{ $t(this.createButtonText) }}</r-form-submit-button>
            </div>
            <r-or-border v-if="office365LoginUrl != ''" class="text-uppercase mt-sm-2">{{ $t("or") }}</r-or-border>
            <div class="buttondiv">
              <!-- ID is used for tracking, do not change! -->
              <v-btn outlined color="grey darken-2" id="signup_create_account_microsoft" v-if="office365LoginUrl != ''" v-on:click="office365signup" data-testid="mssignup-button">
                <v-img class="px-2" src="static/images/ms-logo-64.png" contain max-width="32px" />
                {{ $t("sign_up_with_microsoft") }}</v-btn>
            </div>
          </v-form>
          <div class="footer text-body-1">
            <span v-html="$t('trying_to_join_meeting_signup', { 'webapp_link': 'https://vevox.app' })"></span>
          </div>
          <div v-if="$vuetify.breakpoint.smAndDown" class="mt-2">
            {{ $t('already_have_account') }}
            <a color="white" :href="loginUrl" data-testid="a_1282dee2-6eee-45be-91e3-1fc237943683">{{ $t('signup.sign_in') }}</a>
          </div>
        </r-form-card>
      </v-col>
    </v-row>
    <v-row class=" mx-4 mt-2 d-flex justify-space-around" v-if="$vuetify.breakpoint.mdAndUp">
      <img src="static/images/vevox/signup_G2.png" class="signupBadge"/>
      <trustpilot-widget class="mx-4" />
      <div class="signupBadgeLink my-auto pt-2">
        <a target="_blank" href="https://www.capterra.com/reviews/185114/Vevox?utm_source=vendor&utm_medium=badge&utm_campaign=capterra_reviews_badge%22">
          <img border="0" src="https://assets.capterra.com/badge/e962e58759ab94ce79452a97003f2f8e.svg?v=2111904&p=185114" />
        </a>
      </div>
    </v-row>
  </v-container>
</template>

<script>
let _ = require('lodash');
import { dashboardUserService } from 'reactor-js-core/dist/services/dashboarduserservice.js'
import { signupService } from 'reactor-js-core/dist/services/signupservice.js'
import passwordComplexity from '@/passwordcomplexity'
import emailValidation from '@/emailvalidation'
import { persistentStorage } from 'reactor-js-core/dist/persistentstorage.js'
import analytics from '@/analytics';
import utils from 'reactor-js-core/dist/utils.js'
import TrustpilotWidget from './TrustpilotWidget.vue'

var configs = require('../../../config');
const env = process.env.NODE_ENV;
const config = env === 'development' ? configs.dev : configs.build;

export default {
  name: 'CreateAccount',
  components: {
    TrustpilotWidget
  },
  props: {
    createTitleText: {
      type: String,
      default: "create_your_free_account",
    },
    createButtonText: {
      type: String,
      default: "create_account",
    },
    /*signupReasonTexts: {
      type: Array,
      default: function () {
        return ["signup_trustpilot_reason1", "signup_trustpilot_reason2", "signup_trustpilot_reason3"]
      }
    }*/
  },
  data () {
    return {
      valid: false,
      email: '',
      emailError: '',
      emailRules: [
        email => {
          if (email.trim().length == 0) return this.$t('required');
          return true;
        }
      ],
      password: '',
      passwordRules: [
        password => {
          return passwordComplexity.validatePassword(password, this.$t);
        }
      ],
      error: '',
      office365LoginUrl: '',
      firstName: '',
      lastName: '',
      nameRules: [
        name => {
          if (name.trim().length == 0) return this.$t('required');
          if (name.trim().length > 40) return this.$t('signup.name_too_long');
          return true;
        }
      ],
      step: 1,
      showPassword: false
    }
  },
  computed: {
    loginUrl() {
      if (config.singleLoginPageAddress != null && config.singleLoginPageAddress !== "") {
        return config.singleLoginPageAddress;
      }
      return "https://login.vevox.com";
    }
  },
  methods: {
    validateEmail() {
      var r = emailValidation.validate(this.email, this.$t);
      if (r == true) {
        this.valid = true;
        this.emailError = '';
        return true;
      }
      this.valid = false;
      this.emailError = r;
      return false;
    },
    setError(error) {
      this.error = error;
    },
    office365signup: function() {
      this.setError('');
      var state_json = {
          type: "signup",
      };
      window.location.href = this.office365LoginUrl + "&state=" + encodeURIComponent(JSON.stringify(state_json));
    },
    signup: function (event) {
      this.setError('');
      if (this.$refs.signupform.validate() && this.validateEmail()) {
        signupService.signupNewUser({firstName: this.firstName, lastName: this.lastName, email: this.email, password: this.password}).then((response) => {
          if (!_.isEmpty(response.deactivatedTenancy)) {
            this.$root.confirm(this.$t('email_has_already_been_used_for_free_account'), (confirm) => {
              if (confirm) {
                window.location = "https://login.vevox.com";
              }
            });
          }
          else if (response.ssoLoginURL == null) {
            if (response.loginId == null) {
              if (response.existsOnlyAsOffice365User) {
                this.$root.confirm({
                  message: this.$t('email_only_office365_user'),
                  positiveText: this.$t('continue'),
                  negativeText: this.$t('cancel')
                }, (confirm) => {
                  if (confirm) {
                    this.office365signup();
                  }
                });
              }
              else {
                this.$root.confirm(this.$t('email_has_already_been_used_for_free_account'), (confirm) => {
                  if (confirm) {
                    window.location = "https://login.vevox.com";
                  }
                });
              }
            }
            else {
              this.$root.connectToRegion(response.regionURL);

              analytics.pushVirtualPageview('/signup-form/step-1', 'Sign up form step 1');
              analytics.pushEvent('CreateAccount');
              if (response.organizationTenancy == null) {
                this.$router.push({
                  name: 'SetupAccount',
                  params: {
                    id: response.loginId
                  },
                  query: {
                    region_url: response.regionURL
                  }
                });
              }
              else {
                let org_tenancy = { ...response.organizationTenancy };
                if (org_tenancy.regionURL == null) {
                  org_tenancy.regionURL = response.regionURL;
                }
                this.$router.push({ name: 'RequestAccess', query: { id: response.loginId, tenancy: JSON.stringify(org_tenancy) } });
              }
            }
          }
          else {
            this.$root.confirm(this.$t('organization_already_has_account_sso'), (confirm) => {
              if (confirm) {
                window.location = response.ssoLoginURL;
              }
            });
          }
        }, (failure) => {
          this.setError("Signup failed, please try again.");
        });
      }
    }
  },
  mounted() {
    //Signup through the app
    //this is used to skip the verification e-mail if the user is already coming from an e-mail
    persistentStorage.set("invitationId", this.$route.query.invitation_id);
    dashboardUserService.getAuthenticationTypes(response => {
      if (response.status === "success") {
        if (response.data && response.data != '') {
          for (var i=0; i<response.data.authenticationTypes.length; i++) {
            if (response.data.authenticationTypes[i].authenticationType == "office365") {
              this.office365LoginUrl = response.data.authenticationTypes[i].authenticationURL;
              break;
            }
          }
        }
      } else {
        console.warn("Could not get authentication types");
      }
    });

    if (this.$route.query.invitation_id != null) {
      dashboardUserService.getInvitationDetails({invitationId: this.$route.query.invitation_id}).then((response) => {
        if (response.invitationDetails != null) {
          this.email = response.invitationDetails.email;
        }
      }, (failure) => {
      });
    }
  }
}
</script>

<style scoped lang="scss">
.mainBorder {
  border: 1px solid #e0e0e0;
}
.stepperHeader {
  max-width: 320px;
}

.stepNumber {
  width: 24px;
  height: 24px;
  text-align: center;
  font-size: 0.8rem;
}

.stepIcon {
  vertical-align: baseline;
}

.signupBadge {
  height: 60px;
  margin-top: 20px;
  width: auto;
}

.logo {
  width: 160px;
}

.header {
  font-size: 1.7rem;
}

.footer {
  margin-top: 32px;
  margin-bottom: 16px;
}

.signupBadgeLink {
  width: 180px;
}

.buttondiv {
  text-align: center;
}

@media only screen and (max-height: 800px)  {
  .header {
    font-size: 1.6rem;
  }

  .footer {
    margin-top: 16px;
    margin-bottom: 0px;
  }
  .signupBadge {
    height: 50px;
    margin-top: 10px;
    width: auto;
  }
  .signupBadgeLink {
    width: 150px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/signup/CreateAccount.vue